import { ColDef, IServerSideDatasource } from "ag-grid-community";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import AgGridCustomTable from "../../../../../../../../../util/component/AgGridCustomTable";
import PinnedRowRender from "../../_components/pinned-row-render/PinnedRowRender";
import DashboardHeaderCol from "../../_components/dashboard-header-column/DashboardHeaderCol";
import { mobilityStoreActivitiesByRegionFilterState } from "../../../../../recoil";
import { ISortBy } from "../../../../../../../api/types";
import { IFormValuesMsActivitiesByRegionListDashboardFilter, IMobilityStoreActivitiesByRegion, IMobilityStoreActivitiesByRegionList, ITotalMobilityStoreActivitiesByRegion } from "../../../../../types";
import { ReactQueryKey } from "../../../../../../../types";
import { getMobilityStoresActivitiesByRegion, getTotalMobilityStoresActivitiesByRegion } from "../../../../../../../api/services";
import CustomLoader from "../../../../../../../../../util/component/CustomLoader";

/**
 * Table component of mobility stores by region
 */
const TableMsByRegion = () => {

    const {t} = useTranslation('booking');

    const pendingListFilter = useRecoilValue(mobilityStoreActivitiesByRegionFilterState);

    const queryClient = useQueryClient();

    const mobilityStoresActivitiesListMutation = useMutation(getMobilityStoresActivitiesByRegion);
    const mobilityStoresActivitiesSummaryMutation = useMutation(getTotalMobilityStoresActivitiesByRegion);

    const [pinnedBottomRowData, setPinnedBottomRowData] = useState<ITotalMobilityStoreActivitiesByRegion | undefined>(undefined);
    // Each Column Definition results in one Column.
    const columnDefs = useMemo<ColDef<IMobilityStoreActivitiesByRegion>[]>(() => [
        {
            field: 'region',
            headerName: t('label.mobilityStoreActivitiesForRegionRegionColumn'),
            minWidth: 300,
            suppressColumnsToolPanel: true,
            rowGroup: true,
            lockPosition: "left",
            hide: true
        },
        {
            field: 'city',
            headerName: '',
            minWidth: 300,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            hide: true,
        },
        {
            field: 'inserted',
            headerName: t('label.mobilityStoreActivitiesForRegionInsertedColumn'),
            suppressColumnsToolPanel: true,
            flex: 1,
            resizable: true
        },
        {
            field: 'cancelled',
            headerName: t('label.mobilityStoreActivitiesForRegionCancelledColumn'),
            suppressColumnsToolPanel: true,
            flex: 1,
            resizable: true
        },
        {
            field: 'fleetFail',
            headerName: t('label.mobilityStoreActivitiesForRegionFleetFailColumn'),
            suppressColumnsToolPanel: true,
            flex: 1,
            resizable: true
        },
        {
            field: 'noShow',
            headerName: t('label.mobilityStoreActivitiesForRegionNoShowColumn'),
            suppressColumnsToolPanel: true,
            flex: 1,
            resizable: true
        },
        {
            field: 'customerFail',
            headerName: t('label.mobilityStoreActivitiesForRegionCustomerFailColumn'),
            suppressColumnsToolPanel: true,
            headerTooltip: t("label.mobilityStoreActivitiesForRegionCustomerFailColumnTooltip"),
            flex: 1,
            minWidth: 145,
            resizable: true
        },
        {
            field: 'unfullfilled',
            headerName: t('label.mobilityStoreActivitiesForRegionUnfullfilledColumn'),
            suppressColumnsToolPanel: true,
            headerTooltip: t("label.mobilityStoreActivitiesForRegionUnfullfilledColumnTooltip"),
            flex: 1,
            minWidth: 160,
            resizable: true
        }
    ], [t]);

    // "auto column" is included as the first column in the grid
    const autoGroupColumnDef = useMemo<ColDef>(() => ({
        headerName: t('label.mobilityStoreActivitiesForRegionRegionColumn'),
        minWidth: 200,
        field: 'city',
        resizable: true,
        lockPosition: "left"
    }), []);

    // DefaultColDef sets props common to all Columns
    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: false,
        unSortIcon: true,
        minWidth: 120,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerComponent: DashboardHeaderCol,
        cellRendererSelector: (params: any) => {
            if (params.node.rowPinned) {
                return {
                    component: PinnedRowRender,
                    params: {
                        field: ['city','region'],
                        style: { 'fontWeight': 'bold' },
                    },
                };
            } else {
                // rows that are not pinned don't use any cell renderer
                return undefined;
            }
        }
    }), []);

    // load table record
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                let mobilityStoresActivitiesSummary: ITotalMobilityStoreActivitiesByRegion = {} as ITotalMobilityStoreActivitiesByRegion;
                let rowData: IMobilityStoreActivitiesByRegion[] = [];
                const groupKeys = params.request.groupKeys;

                const start = !!params.request.startRow ? params.request.startRow : 0;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;

                // data region group by list - if not groupKeys
                if (groupKeys.length === 0) {

                    const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                        field: params.request.sortModel[0].colId,
                        order: params.request.sortModel[0].sort
                    } : undefined;

                    // get total for header
                    queryClient.invalidateQueries({
                        queryKey: [ReactQueryKey.TotalHeaderDashboard]
                    });

                    // count for pagination and totals pinned row
                    mobilityStoresActivitiesSummaryMutation.mutateAsync({
                        filters: pendingListFilter as IFormValuesMsActivitiesByRegionListDashboardFilter
                    }).then((response: ITotalMobilityStoreActivitiesByRegion) => {
                        //  set total for pinned row
                        mobilityStoresActivitiesSummary = response;
                        setPinnedBottomRowData(mobilityStoresActivitiesSummary);

                        // get records
                        mobilityStoresActivitiesListMutation.mutateAsync({
                            filters: pendingListFilter as IFormValuesMsActivitiesByRegionListDashboardFilter,
                            sortBy: sortBy,
                            start: start,
                            limit: limit
                        }).then((response: IMobilityStoreActivitiesByRegionList[]) => {
                            const rowData = response
                                .map(elem => {
                                    elem.root['children'] = elem.children;
                                    return elem.root
                                });
                            params.success({
                                rowData: rowData,
                                rowCount: mobilityStoresActivitiesSummary.totalRecords ?? 0
                            });

                            mobilityStoresActivitiesSummary.totalRecords == 0 && params.api.showNoRowsOverlay();
                        }).catch((err: AxiosError) => {
                            params.fail();
                            params.api.hideOverlay();
                        });
                    }).catch((err: AxiosError) => {
                        params.fail();
                        params.api.hideOverlay();
                    });
                }

                // data city list
                if (groupKeys.length === 1 && groupKeys[0]) {
                    rowData = params.parentNode.data.children;

                    params.success({
                        // @ts-ignore
                        rowData: rowData.slice(start,start + limit),
                        rowCount: rowData.length
                    });
                }

                mobilityStoresActivitiesSummary.totalRecords == 0 && params.api.showNoRowsOverlay();
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [pendingListFilter]);

    return (
        <>
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource= {serverSideDatasource}
                paginationPageSize={25}
                rowModelType="serverSide"
                pinnedBottomRowData={[pinnedBottomRowData]}
                suppressMultiSort
                suppressDragLeaveHidesColumns
                cacheBlockSize={25}
                enableCellTextSelection
                animateRows={true}
                autoGroupColumnDef={autoGroupColumnDef}
                tooltipShowDelay={0}
            />
            <CustomLoader
                loading={mobilityStoresActivitiesListMutation.isLoading ||
                mobilityStoresActivitiesSummaryMutation.isLoading} />
        </>
    );
}

export default TableMsByRegion;
